import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { readStoredTokenRaw } from '@domains/shared/domain/utils/auth-storage';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { isJwtExpired, storedToken } from '@domains/shared/domain/utils/jwt';

/**
 * Ohlásí po přihlášení, na čem uživatel aplikaci používá.
 *
 * Posílá se jen rozlišení obrazovky a hlavička prohlížeče; rozebrání na jméno
 * prohlížeče a systém dělá server, aby se to opravovalo na jednom místě a šlo
 * přeparsovat zpětně, až se objeví něco nového.
 *
 * Chyba se schválně polyká: hlášení sestavy je vedlejší kolej a nesmí ničit
 * přihlášení ani ukazovat uživateli hlášku o něčem, co si nevyžádal.
 */
@Injectable({ providedIn: 'root' })
export class DeviceReportService {

  /**
   * Token, se kterým se hlášení povedlo. Ne prosté „už jsem hlásil": dřív se posílalo
   * při prvním uživateli, jenže ten se objeví i na přihlašovací obrazovce dřív, než
   * je v úložišti token — požadavek skončil na 401 a druhý pokus už nepřišel.
   */
  private reportedFor: string | null = null;

  constructor(
    private http: HttpClient,
  ) { }

  report(): void {
    const token = readStoredTokenRaw();

    // Bez tokenu by požadavek stejně skončil na 401; po přihlášení se zavolá znovu.
    if (!token || token === this.reportedFor) {
      return;
    }

    // S vypršelým tokenem taky (EL-2290) — a ta 401 by předběhla AuthGuard: error
    // interceptor by uživatele odvedl na login dřív, než guard stihne vypršelý token
    // uklidit a říct proč.
    const jwt = storedToken();
    if (jwt && isJwtExpired(jwt)) {
      return;
    }

    this.http.post<void>(`${webApiUrl}/User/Device`, {
      screenWidth: window.screen?.width ?? 0,
      screenHeight: window.screen?.height ?? 0,
      userAgent: navigator.userAgent ?? '',
    }, { headers }).subscribe({
      // Zapamatuje se až po úspěchu — jinak by jediné selhání znamenalo, že se
      // sestava do statistik nedostane vůbec.
      next: () => (this.reportedFor = token),
      error: () => undefined,
    });
  }
}
